import React, { useEffect, useState } from 'react';
import { api, fmtNum, fmtDate } from '../lib/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, ChevronDown, ChevronRight, Box, Pencil, Download, ArrowUpDown } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function DatabaseStok() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('fefo_asc'); // Default urutkan dari FEFO Terdekat
  const [expandedRows, setExpandedRows] = useState({});

  // State Dialog Edit Expired
  const [editingLot, setEditingLot] = useState(null);
  const [newExpiredDate, setNewExpiredDate] = useState('');
  const [savingExpired, setSavingExpired] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await api.databaseStok();
      setData(Array.isArray(result) ? result : []);
    } catch (err) {
      toast.error('Gagal memuat Database Stok');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const toggleRow = (reagenId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [reagenId]: !prev[reagenId],
    }));
  };

  // LOGIKA SEARCH + SORT BY DATABASE STOK
  const filteredAndSortedData = data
    .filter((item) =>
      item && item.nama_reagen && item.nama_reagen.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'fefo_asc') {
        const dateA = a.expired_terdekat || (a.lots && a.lots[0]?.expired_date) || '9999-12-31';
        const dateB = b.expired_terdekat || (b.lots && b.lots[0]?.expired_date) || '9999-12-31';
        return dateA.localeCompare(dateB);
      }
      if (sortBy === 'name_asc') {
        return (a.nama_reagen || '').localeCompare(b.nama_reagen || '');
      }
      if (sortBy === 'stock_asc') {
        return (a.total_sisa_test || 0) - (b.total_sisa_test || 0);
      }
      if (sortBy === 'stock_desc') {
        return (b.total_sisa_test || 0) - (a.total_sisa_test || 0);
      }
      if (sortBy === 'lots_desc') {
        return (b.total_lots || 0) - (a.total_lots || 0);
      }
      return 0;
    });

  // =========================================================
  // LOGIKA EXPORT EXCEL FEFO (UNFOLD LOT & SORTING ED ASCENDING)
  // =========================================================
  const handleExportExcel = () => {
    if (!filteredAndSortedData || filteredAndSortedData.length === 0) {
      toast.error('Tidak ada data stok untuk diexport');
      return;
    }

    let exportRows = [];

    filteredAndSortedData.forEach((group) => {
      if (group.lots && group.lots.length > 0) {
        group.lots.forEach((lot) => {
          const info = lot.expiry_info || {};
          exportRows.push({
            nama_reagen: group.nama_reagen,
            no_lot: lot.batch_no || lot.reagent_no || '-',
            sisa_tes: lot.sisa_test || 0,
            expired_date: lot.expired_date || '9999-12-31',
            expired_display: lot.expired_date ? fmtDate(lot.expired_date) : 'Tanpa Expired Date',
            status_fefo: group.fefo_info?.label || 'Tanpa Expired Date',
            sisa_hari: info.days_left !== null && info.days_left !== undefined ? `${info.days_left} hari` : '-'
          });
        });
      } else {
        const fefoExpiredDate = group.expired_terdekat || group.fefo_info?.expired_date;
        exportRows.push({
          nama_reagen: group.nama_reagen,
          no_lot: '-',
          sisa_tes: group.total_sisa_test || 0,
          expired_date: fefoExpiredDate || '9999-12-31',
          expired_display: fefoExpiredDate ? fmtDate(fefoExpiredDate) : 'Tanpa Expired Date',
          status_fefo: group.fefo_info?.label || 'Tanpa Expired Date',
          sisa_hari: '-'
        });
      }
    });

    exportRows.sort((a, b) => new Date(a.expired_date) - new Date(b.expired_date));

    const finalData = exportRows.map((row, index) => ({
      'No': index + 1,
      'Nama Reagen': row.nama_reagen,
      'No. Lot / Batch': row.no_lot,
      'Sisa Tes': row.sisa_tes,
      'Tanggal Expired': row.expired_display,
      'Status Expired': row.status_fefo,
      'Sisa Hari': row.sisa_hari,
    }));

    const worksheet = XLSX.utils.json_to_sheet(finalData);

    worksheet['!cols'] = [
      { wch: 6 },
      { wch: 32 },
      { wch: 20 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Database Stok FEFO');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blobData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const currentDate = new Date().toISOString().split('T')[0];
    const fileName = `Database_Stok_Reagen_FEFO_${currentDate}.xlsx`;

    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    toast.success('Database Stok FEFO berhasil diexport ke Excel');
  };

  const handleEditClick = (lot, reagenName) => {
    setEditingLot({ ...lot, reagen_name: reagenName });
    setNewExpiredDate(lot.expired_date || '');
  };

  const handleSaveExpired = async () => {
    if (!editingLot) return;
    setSavingExpired(true);
    try {
      await api.updateLotExpired(editingLot.id, newExpiredDate);
      toast.success(`Expired date lot/batch diperbarui`);
      setEditingLot(null);
      loadData();
    } catch (err) {
      toast.error('Gagal memperbarui tanggal expired');
    } finally {
      setSavingExpired(false);
    }
  };

  const getBadgeStyle = (color) => {
    switch (color) {
      case 'red':
        return 'bg-red-600 text-white';
      case 'orange':
        return 'bg-orange-500 text-white';
      case 'yellow':
        return 'bg-yellow-500 text-white';
      case 'green':
        return 'bg-emerald-600 text-white';
      default:
        return 'bg-gray-400 text-white';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header & Filter Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Database Stok</h1>
          <p className="text-sm text-muted-foreground">
            Pemantauan fisik stok reagen real-time, pengelompokan lot batch, dan kontrol kadaluarsa (FEFO).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Input Cari */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cari reagen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-[200px] pl-8 text-xs"
            />
          </div>

          {/* Select Sort By */}
          <div className="flex items-center gap-1.5 bg-background border rounded-md px-2.5 h-9">
            <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-full border-0 bg-transparent text-xs font-medium focus:outline-none cursor-pointer"
            >
              <option value="fefo_asc">Expired Terdekat (FEFO)</option>
              <option value="name_asc">Nama Reagen (A - Z)</option>
              <option value="stock_asc">Sisa Stok (Tersedikit)</option>
              <option value="stock_desc">Sisa Stok (Terbanyak)</option>
              <option value="lots_desc">Jumlah Lot (Terbanyak)</option>
            </select>
          </div>

          {/* Button Export */}
          <Button
            onClick={handleExportExcel}
            className="h-9 gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700 text-xs"
            size="sm"
          >
            <Download className="h-4 w-4" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Tabel Pengelompokan Reagen */}
      <Card className="p-0">
        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : filteredAndSortedData.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Tidak ada data stok reagen yang aktif.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-muted-foreground bg-muted/20 border-b">
                  <th className="w-10 px-3 py-2.5 text-center"></th>
                  <th className="px-4 py-2.5 text-left">Nama Reagen</th>
                  <th className="px-3 py-2.5 text-center">Jumlah Lot</th>
                  <th className="px-3 py-2.5 text-right">Total Sisa Tes</th>
                  <th className="px-3 py-2.5 text-left">Expired Terdekat (FEFO)</th>
                  <th className="px-3 py-2.5 text-center">Status Expired</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedData.map((group) => {
                  const isExpanded = !!expandedRows[group.reagen_id];
                  const fefo = group.fefo_info || {};

                  const fefoExpiredDate =
                    group.expired_terdekat ||
                    (group.lots && group.lots.length > 0 ? group.lots[0].expired_date : null) ||
                    fefo.expired_date;

                  return (
                    <React.Fragment key={group.reagen_id}>
                      {/* Baris Utama Reagen */}
                      <tr
                        onClick={() => toggleRow(group.reagen_id)}
                        className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <td className="px-3 py-3 text-center text-muted-foreground">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-4 py-3 font-semibold text-foreground">
                          <div className="flex items-center gap-2">
                            <Box className="h-4 w-4 text-primary" />
                            {group.nama_reagen}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Badge variant="outline" className="font-medium">
                            {group.total_lots} Lot
                          </Badge>
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-primary num">
                          {fmtNum(group.total_sisa_test)} test
                        </td>
                        <td className="px-3 py-3 text-left font-medium">
                          {fefoExpiredDate ? (
                            fmtDate(fefoExpiredDate)
                          ) : (
                            <span className="text-muted-foreground italic">Tanpa Expired Date</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Badge className={getBadgeStyle(fefo.color)}>
                            {fefo.label || 'Tanpa Expired Date'}
                          </Badge>
                        </td>
                      </tr>

                      {/* Sub-Tabel Breakdown Lot */}
                      {isExpanded && (
                        <tr className="bg-muted/10 border-b">
                          <td colSpan={6} className="p-4 pl-12">
                            <div className="rounded-md border bg-background p-3 space-y-2">
                              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                Detail Lot / Batch Penerimaan ({group.nama_reagen})
                              </div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b bg-muted/30 text-muted-foreground">
                                    <th className="px-3 py-1.5 text-left">Tanggal Terima</th>
                                    <th className="px-3 py-1.5 text-left">Lot / Batch</th>
                                    <th className="px-3 py-1.5 text-right">Tes Awal</th>
                                    <th className="px-3 py-1.5 text-right">Sisa Tes</th>
                                    <th className="px-3 py-1.5 text-left">Tanggal Expired</th>
                                    <th className="px-3 py-1.5 text-center">Sisa Hari</th>
                                    <th className="px-3 py-1.5 text-center">Aksi</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.lots.map((lot) => {
                                    const info = lot.expiry_info || {};
                                    return (
                                      <tr key={lot.id} className="border-b hover:bg-muted/20">
                                        <td className="px-3 py-2">{fmtDate(lot.tanggal_terima)}</td>
                                        <td className="px-3 py-2 font-mono">{lot.batch_no || lot.reagent_no || '-'}</td>
                                        <td className="px-3 py-2 text-right num">{fmtNum(lot.total_test_awal)}</td>
                                        <td className="px-3 py-2 text-right num font-semibold text-emerald-600">
                                          {fmtNum(lot.sisa_test)} test
                                        </td>
                                        <td className="px-3 py-2 font-medium">
                                          {lot.expired_date ? fmtDate(lot.expired_date) : (
                                            <span className="text-amber-600 font-normal">Tanpa Expired Date</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-center num">
                                          {info.days_left !== null && info.days_left !== undefined
                                            ? `${info.days_left} hari`
                                            : '-'}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2 text-xs"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditClick(lot, group.nama_reagen);
                                            }}
                                          >
                                            <Pencil className="h-3 w-3 mr-1 text-primary" /> Edit Expired
                                          </Button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* DIALOG EDIT EXPIRED DATE LOT */}
      <Dialog open={!!editingLot} onOpenChange={(o) => !o && setEditingLot(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Expired Date Lot</DialogTitle>
          </DialogHeader>
          {editingLot && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nama Reagen</Label>
                <Input value={editingLot.reagen_name} disabled className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Lot / Batch No</Label>
                  <Input value={editingLot.batch_no || editingLot.reagent_no || '-'} disabled className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Sisa Tes Aktif</Label>
                  <Input value={`${editingLot.sisa_test} test`} disabled className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Tanggal Expired Baru</Label>
                <Input
                  type="date"
                  value={newExpiredDate}
                  onChange={(e) => setNewExpiredDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLot(null)}>
              Batal
            </Button>
            <Button onClick={handleSaveExpired} disabled={savingExpired}>
              {savingExpired ? 'Menyimpan...' : 'Simpan Expired'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
